import React, { useEffect, useState, useMemo } from "react";
import withRouter from "components/Common/withRouter";
import TableContainer from "components/Common/TableContainer";
import {
  Card, CardBody, Col, Container, Row, Badge, Spinner, Button,
  Modal, ModalHeader, ModalBody, Form, FormGroup, Label, Input
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import axios from "axios";
import moment from "moment";
import toastr from "toastr";
import "toastr/build/toastr.min.css";

const API_BASE = "https://maasradarapi.stechomeyazilim.info";

const CommunityPostsList = () => {
  document.title = "Topluluk Gönderileri | Maaş Radar Admin Paneli";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  const [detailModal, setDetailModal] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  const [editModal, setEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});

  function getAuthHeaders() {
    const token = localStorage.getItem("token");
    return { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };
  }

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/CommunityPosts?$orderby=CreatedAt desc&$top=200`, { headers: getAuthHeaders() });
      setItems(res.data.value || []);
    } catch (error) {
      console.error("Veriler alınamadı", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const showToast = (success, message) => {
    toastr.options = { positionClass: "toast-top-right", timeOut: 5000, closeButton: true, progressBar: true };
    if (success) toastr.success(message, "Başarılı");
    else toastr.error(message, "Hata");
  };

  const handleDelete = (id) => {
    if (!window.confirm("Bu gönderiyi silmek istediğinize emin misiniz?")) return;
    setProcessing(p => ({ ...p, [id + "delete"]: true }));
    axios.patch(`${API_BASE}/CommunityPosts(${id})`, { IsDeleted: true }, { headers: getAuthHeaders() })
      .then(() => { showToast(true, "Gönderi silindi."); fetchItems(); })
      .catch((err) => { console.error(err); showToast(false, "Silme sırasında hata oluştu."); })
      .finally(() => setProcessing(p => ({ ...p, [id + "delete"]: false })));
  };

  const openDetail = (item) => { setDetailItem(item); setDetailModal(true); };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      Content: item.Content || "",
      IsDeleted: item.IsDeleted === true,
    });
    setEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    setProcessing(p => ({ ...p, [editItem.Id + "save"]: true }));
    try {
      await axios.patch(`${API_BASE}/CommunityPosts(${editItem.Id})`, editForm, { headers: getAuthHeaders() });
      showToast(true, "Gönderi güncellendi.");
      setEditModal(false);
      fetchItems();
    } catch (err) {
      console.error(err);
      showToast(false, "Güncelleme sırasında hata oluştu.");
    } finally {
      setProcessing(p => ({ ...p, [editItem.Id + "save"]: false }));
    }
  };

  const columns = useMemo(() => [
    { Header: "ID", accessor: "Id", width: "80px" },
    { Header: "İçerik", accessor: "Content", Cell: ({ value }) => value ? value.substring(0, 60) + (value.length > 60 ? "..." : "") : "-" },
    { Header: "Yorum", accessor: "CommentsCount" },
    { Header: "Beğeni", accessor: "LikesCount" },
    { Header: "Durum", accessor: "IsDeleted", Cell: ({ value }) => value ? <Badge color="danger">Silinmiş</Badge> : <Badge color="success">Aktif</Badge> },
    { Header: "Tarih", accessor: "CreatedAt", Cell: ({ value }) => value ? moment(value).format("DD.MM.YYYY") : "-" },
    {
      Header: "İşlemler", accessor: "actions", disableSortBy: true,
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <Button size="sm" color="info" onClick={() => openDetail(row.original)}>Detay</Button>
          <Button size="sm" color="primary" onClick={() => openEdit(row.original)}>Düzenle</Button>
          <Button size="sm" color="danger" disabled={processing[row.original.Id + "delete"]} onClick={() => handleDelete(row.original.Id)}>
            {processing[row.original.Id + "delete"] ? <Spinner size="sm" /> : "Sil"}
          </Button>
        </div>
      )
    }
  ], [processing]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Topluluk Gönderileri" breadcrumbItem="Liste" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer columns={columns} data={items} isGlobalFilter={true} isAddOptions={false} customPageSize={10} className="custom-header-css" />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="lg">
        <ModalHeader toggle={() => setDetailModal(false)}>Gönderi Detayı</ModalHeader>
        <ModalBody>
          {detailItem && (
            <div>
              <p><strong>ID:</strong> {detailItem.Id}</p>
              <p><strong>İçerik:</strong> {detailItem.Content}</p>
              <p><strong>Yorum Sayısı:</strong> {detailItem.CommentsCount}</p>
              <p><strong>Beğeni Sayısı:</strong> {detailItem.LikesCount}</p>
              <p><strong>Tarih:</strong> {detailItem.CreatedAt ? moment(detailItem.CreatedAt).format("DD.MM.YYYY HH:mm") : "-"}</p>
            </div>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={editModal} toggle={() => setEditModal(false)} size="lg">
        <ModalHeader toggle={() => setEditModal(false)}>Gönderi Düzenle</ModalHeader>
        <ModalBody>
          {editItem && (
            <Form>
              <FormGroup><Label>İçerik</Label><Input type="textarea" rows={6} value={editForm.Content} onChange={e => setEditForm({...editForm, Content: e.target.value})} /></FormGroup>
              <FormGroup check className="mb-3"><Label check><Input type="checkbox" checked={editForm.IsDeleted} onChange={e => setEditForm({...editForm, IsDeleted: e.target.checked})} /> Silinmiş</Label></FormGroup>
              <Button color="primary" disabled={processing[editItem.Id + "save"]} onClick={handleSaveEdit}>
                {processing[editItem.Id + "save"] ? <Spinner size="sm" /> : "Kaydet"}
              </Button>
            </Form>
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default withRouter(CommunityPostsList);
